import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { ReactComponent as DeleteIcon } from 'src/pages/AIListening/Main/CampaignCard/svgs/trash.svg'
import { ReactComponent as FileIcon } from 'src/assets/svg/blue-file-download.svg'
import { ReactComponent as ModalCloseIcon } from 'src/assets/svg/modal-close.svg'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import useOutsideClick from 'src/hooks/useOutsideClick'
import {
  PLATFORM_LABEL,
  PLATFORM_ORDER,
} from 'src/pages/ReviewAnalysis/constants'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import { useAddReviewAnalysisPlatformsBatchMutation } from 'src/reactQuery/useAddReviewAnalysisPlatformsBatchMutation'
import { useUpdateReviewAnalysisPlatformMutation } from 'src/reactQuery/useUpdateReviewAnalysisPlatformMutation'
import { ReviewPlatform } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './addReviewPlatformModal.module.scss'
import { PasteLinksModal } from '../PasteLinksModal'

type Platform = ReviewPlatform

interface AddReviewPlatformModalProps {
  initialCardId?: string
  initialFacilityId?: string
  initialFacilityName?: string
  initialLinks?: string[]
  initialPlatform?: Platform
  initialTitle?: string
  isOpen: boolean
  lockFacility?: boolean
  mode?: 'add' | 'edit'
  onClose: () => void
  onError?: () => void
  onSuccess?: () => void
}

interface UploadedFile {
  format: string
  name: string
  size: string
  status: string
  uploadedDate: string
}

interface AddedPlatform {
  facilityId?: string
  facilityName: string
  placeId: string
  platform: Platform
  title: string
}

// Built from the shared catalog so a new platform only has to be declared once
// (constants.ts + platformIcons.ts) to appear in the picker.
const PLATFORMS: {
  icon: FC<React.SVGProps<SVGSVGElement>>
  id: Platform
  label: string
}[] = PLATFORM_ORDER.map((id) => ({
  icon: getPlatformIcon(id),
  id,
  label: PLATFORM_LABEL[id],
}))

// App-store listings have no Place ID and aren't on a map — they're targeted by
// their store URL (or bare package id / app id) instead.
const APP_STORE_PLATFORMS: Platform[] = ['playstore', 'appstore']

// Icons whose own colours must survive: the selected chip turns its icon white
// so mono logos read on the purple background, which destroys a multi-colour
// mark (App Store became a plain white square — its gradient tile and its white
// Apple glyph were both filled white).
const BRAND_COLOUR_ICONS: Platform[] = ['google', 'playstore', 'appstore']

const iconClass = (platform: Platform, base: string): string =>
  `${base} ${BRAND_COLOUR_ICONS.includes(platform) ? styles.brandIcon : ''}`

const APP_STORE_HINT: Partial<Record<Platform, string>> = {
  appstore: 'https://apps.apple.com/ge/app/bank-of-georgia/id1234567890',
  playstore: 'https://play.google.com/store/apps/details?id=ge.bog.mobilebank',
}

const getPlaceIdFromLink = (value: string): string => {
  const v = value.trim()
  if (!v) return ''

  try {
    const url = new URL(v)
    const placeId = url.searchParams.get('q')?.match(/^place_id:(.+)$/)?.[1]
    return placeId ? decodeURIComponent(placeId) : v
  } catch {
    return v
  }
}

const getPlaceHref = (value: string): string => {
  const v = value.trim()
  if (!v) return '#'

  try {
    return new URL(v).toString()
  } catch {
    return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(v)}`
  }
}

export const AddReviewPlatformModal: FC<AddReviewPlatformModalProps> = ({
  initialCardId,
  initialFacilityId,
  initialFacilityName,
  initialLinks = [],
  initialPlatform = 'google',
  initialTitle,
  isOpen,
  lockFacility = false,
  mode = 'add',
  onClose,
  onError,
  onSuccess,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const [facilityName, setFacilityName] = useState(initialFacilityName ?? '')
  const [activePlatform, setActivePlatform] =
    useState<Platform>(initialPlatform)
  const [title, setTitle] = useState(initialTitle ?? '')
  const [placeId, setPlaceId] = useState('')
  const [addedPlatforms, setAddedPlatforms] = useState<AddedPlatform[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [isPasteLinksOpen, setIsPasteLinksOpen] = useState(false)
  // Why Enter didn't add the link. Without this the modal is a dead end: the
  // handler just returns and "Add Location" stays disabled with no explanation.
  const [addError, setAddError] = useState<string | null>(null)
  const isEditMode = mode === 'edit'
  const isAppStorePlatform = APP_STORE_PLATFORMS.includes(activePlatform)

  const addPlatforms = useAddReviewAnalysisPlatformsBatchMutation(campaignId, {
    onError: () => {
      onError?.()
    },
    onSuccess: (response) => {
      const hasFailure = response.results.some((r) => r.status === 'failed')
      if (hasFailure) {
        onError?.()
      } else {
        onSuccess?.()
      }
    },
  })
  const updatePlatform = useUpdateReviewAnalysisPlatformMutation(campaignId, {
    onError: () => {
      onError?.()
    },
    onSuccess: () => {
      onSuccess?.()
    },
  })

  const syncEditPreview = (
    overrides: Partial<AddedPlatform> = {},
    nextPlaceId = placeId
  ) => {
    if (!isEditMode) return
    setAddedPlatforms([
      {
        facilityId: initialFacilityId,
        facilityName:
          overrides.facilityName ??
          facilityName.trim() ??
          initialFacilityName ??
          '',
        placeId: overrides.placeId ?? nextPlaceId,
        platform: overrides.platform ?? activePlatform,
        title: overrides.title ?? title.trim(),
      },
    ])
  }

  // Initialize the form from props ONLY when the modal transitions to open.
  // Re-running this on every render (some initial* props are fresh references
  // each render, e.g. the `initialLinks = []` default) would snap the form back
  // to its initial state — which made platform-tab selection appear broken
  // (clicking Tripadvisor reset straight back to Google).
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setFacilityName(initialFacilityName ?? '')
      setActivePlatform(initialPlatform)
      setTitle(initialTitle ?? '')
      // Pre-fill the Place ID input in edit mode so Save Edit preserves the existing
      // link. Leaving it '' made a rename persist placeIds:[] and wipe the link.
      setPlaceId(isEditMode ? getPlaceIdFromLink(initialLinks[0] ?? '') : '')
      setAddedPlatforms(
        isEditMode && initialFacilityName
          ? [
              {
                facilityId: initialFacilityId,
                facilityName: initialFacilityName,
                placeId: getPlaceIdFromLink(initialLinks[0] ?? ''),
                platform: initialPlatform,
                title: initialTitle ?? '',
              },
            ]
          : []
      )
    }
    wasOpenRef.current = isOpen
  }, [
    initialFacilityId,
    initialFacilityName,
    initialLinks,
    initialPlatform,
    initialTitle,
    isEditMode,
    isOpen,
  ])

  const handleClose = () => {
    setIsPasteLinksOpen(false)
    setAddError(null)
    setAddedPlatforms([])
    setFacilityName(initialFacilityName ?? '')
    setActivePlatform(initialPlatform)
    setTitle(initialTitle ?? '')
    setPlaceId('')
    setUploadedFiles([])
    onClose()
  }

  const handleAddLocation = () => {
    if (addedPlatforms.length === 0) return
    addPlatforms.mutate(
      {
        files: uploadedFiles,
        platforms: addedPlatforms.map((p) => ({
          facilityId: p.facilityId,
          facilityName: p.facilityName,
          placeIds: [p.placeId],
          platform: p.platform,
          title: p.title || undefined,
        })),
      },
      {
        onSettled: () => {
          handleClose()
        },
      }
    )
  }

  const handleSaveEdit = () => {
    if (!initialCardId || !facilityName.trim() || !title.trim()) return
    updatePlatform.mutate(
      {
        cardId: initialCardId,
        payload: {
          facilityName: facilityName.trim(),
          placeIds: placeId.trim() ? [placeId.trim()] : [],
          platform: activePlatform,
          title: title.trim(),
          ...(uploadedFiles.length > 0 ? { files: uploadedFiles } : {}),
        },
      },
      {
        onSettled: () => {
          handleClose()
        },
      }
    )
  }

  useOutsideClick({
    onOutsideClick: isPasteLinksOpen ? () => undefined : handleClose,
    ref,
  })
  useDisableScroll(isOpen)

  if (!isOpen) return null

  const handlePlaceIdKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    if (isEditMode) {
      e.preventDefault()
      syncEditPreview({ placeId: placeId.trim() }, placeId.trim())
      return
    }
    if (!facilityName.trim() || !placeId.trim()) {
      e.preventDefault()
      setAddError(
        !facilityName.trim()
          ? 'Enter a Facility Name first, then press Enter to add this link.'
          : isAppStorePlatform
            ? 'Enter the store listing URL to add.'
            : 'Enter a Place ID to add.'
      )
      return
    }
    setAddError(null)
    setAddedPlatforms((prev) => [
      ...prev,
      {
        facilityId: initialFacilityId,
        facilityName: facilityName.trim(),
        placeId: placeId.trim(),
        platform: activePlatform,
        title: title.trim(),
      },
    ])
    setPlaceId('')
  }

  return (
    <>
      {createPortal(
        <div
          className={styles.overlay}
          style={{ display: isPasteLinksOpen ? 'none' : undefined }}
        >
          <div className={styles.modal} ref={ref}>
            {/* Header */}
            <div className={styles.header}>
              <h2 className={styles.title}>Add Review Platform</h2>
              <button
                className={styles.closeBtn}
                onClick={handleClose}
                type="button"
              >
                <ModalCloseIcon />
              </button>
            </div>

            {/* Scrollable body */}
            <div className={styles.body}>
              {/* Facility Name */}
              <div className={styles.field}>
                <label className={styles.label} htmlFor="facilityName">
                  Facility Name
                </label>
                <input
                  className={styles.input}
                  disabled={lockFacility}
                  id="facilityName"
                  onChange={(e) => {
                    setFacilityName(e.target.value)
                    if (e.target.value.trim()) setAddError(null)
                    syncEditPreview({ facilityName: e.target.value.trim() })
                  }}
                  placeholder=""
                  type="text"
                  value={facilityName}
                />
              </div>

              {/* Platform tabs */}
              <div className={styles.field}>
                <p className={styles.label}>
                  Connect your business locations from:
                </p>
                <div className={styles.platformTabs}>
                  {PLATFORMS.map(({ icon: Icon, id, label }) => (
                    <button
                      className={`${styles.platformTab} ${activePlatform === id ? styles.platformTabActive : ''}`}
                      key={id}
                      onClick={() => {
                        setActivePlatform(id)
                        syncEditPreview({ platform: id })
                      }}
                      type="button"
                    >
                      <Icon className={iconClass(id, styles.platformIcon)} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title + Place ID */}
              <div className={styles.locationBox}>
                <div className={styles.titleField}>
                  <label className={styles.fieldLabel} htmlFor="locationTitle">
                    Title
                  </label>
                  <input
                    className={styles.inlineInput}
                    id="locationTitle"
                    onChange={(e) => {
                      setTitle(e.target.value)
                      syncEditPreview({ title: e.target.value.trim() })
                    }}
                    placeholder="Enter title"
                    type="text"
                    value={title}
                  />
                </div>

                <div className={styles.placeIdSection}>
                  <div className={styles.placeIdHeader}>
                    <span className={styles.targetIcon}>◎</span>
                    <span className={styles.placeIdLabel}>
                      {isAppStorePlatform
                        ? 'Enter Store Listing URL to Target'
                        : 'Enter Place ID to Target'}
                    </span>
                  </div>
                  <input
                    className={styles.input}
                    onChange={(e) => {
                      setPlaceId(e.target.value)
                      syncEditPreview(
                        { placeId: e.target.value.trim() },
                        e.target.value.trim()
                      )
                    }}
                    onKeyDown={handlePlaceIdKeyDown}
                    placeholder={
                      isAppStorePlatform
                        ? 'Enter store listing URL'
                        : 'Enter Place ID'
                    }
                    type="text"
                    value={placeId}
                  />
                  {addError ? (
                    <p className={styles.addError}>{addError}</p>
                  ) : (
                    <p className={styles.pressEnterHint}>
                      Press <kbd className={styles.kbd}>Enter</kbd> to add new
                      link field
                    </p>
                  )}
                </div>

                {/* Map — a store listing isn't a place, so it's map-less. */}
                {!isAppStorePlatform && (
                  <div className={styles.mapWrapper}>
                    <iframe
                      allowFullScreen
                      className={styles.map}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d11905.123456789!2d44.7833!3d41.7151!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sge!4v1234567890"
                      title="Google Maps"
                    />
                  </div>
                )}

                <p className={styles.mapHint}>
                  {/* Icon is its own grid cell so both text rows align to the
                      same column — see .mapHint. */}
                  <span className={styles.infoIcon}>ℹ</span>
                  <span className={styles.mapHintRow}>
                    {isAppStorePlatform
                      ? 'Open the app on its store and copy the listing URL.'
                      : 'Search for your venue on map above and copy your Place ID.'}
                  </span>
                  <span className={styles.mapHintSecondRow}>
                    Suggested format:{' '}
                    <span className={styles.placeIdExample}>
                      {isAppStorePlatform
                        ? APP_STORE_HINT[activePlatform]
                        : 'ChIJu7yy4DlzREARbpVJRWLU1y0'}
                    </span>
                  </span>
                </p>
              </div>

              {/* Added Platforms */}
              <div className={styles.field}>
                <div className={styles.addedHeader}>
                  <p className={styles.label}>Added Platforms</p>
                  <span className={styles.addedCount}>
                    {addedPlatforms.length} Added
                  </span>
                </div>
                {addedPlatforms.length === 0 ? (
                  <div className={styles.addedEmpty}>
                    No platforms added yet. Select a platform above and enter a
                    link.
                  </div>
                ) : (
                  <div className={styles.addedList}>
                    {addedPlatforms.map((p, idx) => {
                      const meta = PLATFORMS.find((pl) => pl.id === p.platform)
                      const Icon = meta?.icon
                      return (
                        <div className={styles.addedItem} key={idx}>
                          <div className={styles.addedItemMain}>
                            <div className={styles.addedItemLeft}>
                              {Icon && (
                                <Icon
                                  className={iconClass(
                                    p.platform,
                                    styles.addedItemIcon
                                  )}
                                />
                              )}
                              <div className={styles.addedItemText}>
                                <span className={styles.addedItemName}>
                                  {meta?.label ?? p.platform}
                                </span>
                                <div className={styles.addedItemMetaRow}>
                                  <span className={styles.addedItemMeta}>
                                    Facility Name:{' '}
                                    <strong>{p.facilityName}</strong>
                                  </span>
                                  <span
                                    className={styles.addedItemLinkWrap}
                                    title={`${p.platform}/${p.placeId}`}
                                  >
                                    <span className={styles.addedItemLinkLabel}>
                                      link:
                                    </span>
                                    <a
                                      className={styles.addedItemLinkText}
                                      href={getPlaceHref(p.placeId)}
                                      rel="noreferrer"
                                      target="_blank"
                                    >
                                      {p.platform}/{p.placeId}
                                    </a>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            className={styles.addedDeleteBtn}
                            onClick={() =>
                              setAddedPlatforms((prev) =>
                                prev.filter((_, i) => i !== idx)
                              )
                            }
                            type="button"
                          >
                            <DeleteIcon className={styles.deleteIcon} />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Data Sources */}
              <div className={styles.field}>
                <p className={styles.label}>Data Sources</p>
                <div className={styles.dataSources}>
                  {/* Upload box */}
                  <div className={styles.dataSourceBox}>
                    {uploadedFiles.length > 0 ? (
                      <>
                        <div className={styles.dataSourceContent}>
                          {uploadedFiles.map((file, idx) => (
                            <div className={styles.uploadedFile} key={idx}>
                              <div className={styles.uploadedFileName}>
                                <FileIcon className={styles.fileIcon} />
                                <span>{file.name}</span>
                              </div>
                              <p className={styles.dataSourceMeta}>
                                <span className={styles.metaLabel}>
                                  Format:{' '}
                                </span>
                                <strong>{file.format}</strong>
                                <span className={styles.metaSep}> | </span>
                                <span className={styles.metaLabel}>Size: </span>
                                <strong>{file.size}</strong>
                              </p>
                              <p className={styles.dataSourceMeta}>
                                <span className={styles.metaLabel}>
                                  Uploaded:{' '}
                                </span>
                                <strong>{file.uploadedDate}</strong>
                                <span className={styles.metaSep}> </span>
                                <span className={styles.metaLabel}>
                                  Status:{' '}
                                </span>
                                <strong>{file.status}</strong>
                              </p>
                            </div>
                          ))}
                        </div>
                        <button
                          className={styles.dataSourceAction}
                          onClick={() => setUploadedFiles([])}
                          type="button"
                        >
                          <DeleteIcon className={styles.deleteIcon} />
                          Delete
                        </button>
                      </>
                    ) : (
                      <>
                        <div className={styles.dataSourceContent}>
                          <p className={styles.dataSourceText}>
                            Upload <strong>documents</strong> or{' '}
                            <strong>spreadsheets</strong> using the
                            location-specific links.
                          </p>
                          <p className={styles.dataSourceMeta}>
                            <span className={styles.metaLabel}>
                              Supported:{' '}
                            </span>
                            <strong>PDF, DOCX, TXT, CSV, XLSX</strong>
                          </p>
                          <p className={styles.dataSourceMeta}>
                            <span className={styles.metaLabel}>
                              File size limit:{' '}
                            </span>
                            <strong>20 MB</strong>
                          </p>
                        </div>
                        <label
                          className={styles.dataSourceAction}
                          htmlFor="fileUpload"
                        >
                          <span className={styles.actionIcon}>⊙</span>
                          Upload Document
                          <input
                            accept=".pdf,.docx,.txt,.csv,.xlsx"
                            hidden
                            id="fileUpload"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (!file) return
                              const ext =
                                file.name.split('.').pop()?.toUpperCase() ??
                                'FILE'
                              const sizeMB = (
                                file.size /
                                (1024 * 1024)
                              ).toFixed(1)
                              const date = new Date().toLocaleDateString(
                                'en-US'
                              )
                              setUploadedFiles([
                                {
                                  format: ext,
                                  name: file.name,
                                  size: `${sizeMB} MB`,
                                  status: 'Completed',
                                  uploadedDate: date,
                                },
                              ])
                              e.target.value = ''
                            }}
                            type="file"
                          />
                        </label>
                      </>
                    )}
                  </div>

                  {/* Paste links box */}
                  <div className={styles.dataSourceBox}>
                    <div className={styles.dataSourceContent}>
                      <p className={styles.dataSourceText}>Paste links.</p>
                      <p className={styles.dataSourceMeta}>
                        <span className={styles.metaLabel}>Supported: </span>
                        <strong>Plain text, URLs</strong>
                      </p>
                      <p className={styles.dataSourceMeta}>
                        <span className={styles.metaLabel}>Max length: </span>
                        <strong>10,000 characters</strong>
                      </p>
                    </div>
                    <button
                      className={styles.dataSourceAction}
                      onClick={() => {
                        setIsPasteLinksOpen(true)
                      }}
                      type="button"
                    >
                      <span className={styles.actionIcon}>⧉</span>
                      Paste links
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={styles.footer}>
              <button
                className={styles.cancelBtn}
                onClick={handleClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className={styles.addBtn}
                disabled={
                  isEditMode
                    ? !initialCardId ||
                      !facilityName.trim() ||
                      !title.trim() ||
                      updatePlatform.isPending
                    : addedPlatforms.length === 0 || addPlatforms.isPending
                }
                onClick={isEditMode ? handleSaveEdit : handleAddLocation}
                type="button"
              >
                {isEditMode ? 'Save Edit' : 'Add Location'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <PasteLinksModal
        isOpen={isPasteLinksOpen}
        onBack={() => setIsPasteLinksOpen(false)}
        onClose={() => setIsPasteLinksOpen(false)}
        onContinue={() => {
          // TODO: handle pasted links
        }}
      />
    </>
  )
}
